// ---- Quadruped archetype ----
// The shared four-legged anatomy (barrel body, four legs + hooves, neck + head),
// parameterised by proportions + palette. Rounded build (plan 28 stage 2):
// capsule legs + barrel, ball head — same option surface, limb pivots, and
// anchor formulas as the original boxy build, so gait.ts and every doc's
// layered `parts` are untouched. src/npc/creature.ts re-exports for its
// existing callers; the model layer (index.ts) drives it from JSON body params
// and layers a model's `parts` on the anchor points it returns.

import type { Prims } from "../meshkit.ts";
import { shade } from "../color.ts";
import { ARCHETYPE_ANCHORS, type Vec3 } from "./parts.ts";

/** Proportions + palette for the shared four-legged body. All sizes optional. */
export type QuadrupedOptions = {
  /** Main hide colour for the barrel body. */
  bodyColor: number;
  /** Leg colour (default: body colour). */
  legColor?: number;
  /** Head colour (default: body colour). */
  headColor?: number;
  /** Hoof colour (default: a dark shade). */
  hoofColor?: number;
  /** Body length along the facing axis (+z). Default 1.5. */
  length?: number;
  /** Body width across (x). Default 0.86. */
  width?: number;
  /** Body height (y). Default 0.78. */
  thickness?: number;
  /** Standing leg height (feet at y=0). Default 0.64. */
  legHeight?: number;
  /** Leg radius. Default 0.13. */
  legRadius?: number;
  /** Cube head size. Default 0.5. */
  headSize?: number;
  /** How far the head juts forward of the body front, as a fraction of headSize.
   *  Default 0.55. */
  neck?: number;
  /** Extra head height above the default seat (world units) — an arched, raised
   *  neck (horse). Default 0. */
  neckRaise?: number;
  /** Wrap the neck + head in a `limb("neck")` pivot at the chest so the gait
   *  can nod/extend the head. Doc parts that must ride it (ears, mane, eyes)
   *  set their `limb: "neck"`. Default off. */
  neckJoint?: boolean;
  /** Publish an empty `limb("tail")` pivot at the rump for doc-authored tail
   *  geometry (parts with `limb: "tail"`) the gait can sway. Default off. */
  tailJoint?: boolean;
};

/** Reference points on a built quadruped, so details can attach features. */
export type QuadAnchors = {
  /** Centre of the head cube (world-local; +z is forward/facing). */
  headX: number; headY: number; headZ: number;
  /** Half the head's edge length (for sizing ears/horns/snouts). */
  headHalf: number;
  /** Top surface of the barrel body (for fleece / saddles). */
  backY: number;
  /** Front (+z) and rear (-z) faces of the body. */
  frontZ: number; rearZ: number;
  /** Body width across x and the standing height of the back. */
  bodyW: number;
};

/**
 * Build the shared four-legged body into `p` (feet at y=0, facing +z) and return
 * anchor points for layering species-specific features. Pure meshkit prims, so a
 * creature is authored exactly like a deco/NPC.
 */
export function quadruped(p: Prims, o: QuadrupedOptions): QuadAnchors {
  const { bone, ball, lathe, limb } = p;
  const len = o.length ?? 1.5;
  const wid = o.width ?? 0.86;
  const thick = o.thickness ?? 0.78;
  const legH = o.legHeight ?? 0.64;
  const legR = o.legRadius ?? 0.13;
  const headSz = o.headSize ?? 0.5;
  const legCol = o.legColor ?? o.bodyColor;
  const headCol = o.headColor ?? o.bodyColor;
  const hoofCol = o.hoofColor ?? shade(legCol, -0.5);

  const bodyY = legH + thick / 2;     // barrel centre
  const halfLen = len / 2;

  // ---- Legs at the four corners (inset so they sit under the body) ----
  // Each leg is its own pivot group at the hip (top of the leg) so the shared
  // gait animator can swing it fore/aft — legFL/legFR (front, +z) + legBL/legBR
  // (back). Naming: F/B = front/back along the facing axis, L/R = the −x/+x side.
  const lx = wid / 2 - legR;
  const lz = halfLen - legR * 1.6;
  const hoofH = legR * 0.62;
  // Hip seat: at the legs' inset x the barrel's belly curves well ABOVE legH on
  // wide bodies, so a thigh starting exactly at legH floats in a visible gap.
  // Start (and pivot) the thigh up inside the barrel instead — the leg swings
  // from the body like a real hip and its top cap stays buried mid-stride.
  const hipY = legH + thick * 0.22;
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const suffix = `${sz > 0 ? "F" : "B"}${sx < 0 ? "L" : "R"}`;
      const name = `leg${suffix}`;
      const hoof = () =>
        // Hoof: a squat flared lathe.
        lathe(sx * lx, 0, sz * lz, [
          [legR * 1.14, 0], [legR * 1.18, hoofH * 0.5], [legR, hoofH * 0.9], [0, hoofH],
        ], hoofCol);
      limb(name, [sx * lx, hipY, sz * lz], () => {
        // Two-segment leg (the archetype standard): thigh/gaskin hip→joint,
        // then a nested knee (front) / hock (rear) pivot carrying the cannon +
        // hoof, so the gait folds the lower leg mid-stride. The hock sits
        // higher than the carpus, like the real anatomy reads in silhouette.
        const jointY = legH * (sz > 0 ? 0.48 : 0.58);
        bone(sx * lx, hipY, sz * lz, sx * lx, jointY - legR * 0.2, sz * lz,
          legR * 1.2, legR * 0.78, legCol, undefined, { amount: 0.28, at: 0.3 });
        limb(`knee${suffix}`, [sx * lx, jointY, sz * lz], () => {
          bone(sx * lx, jointY, sz * lz, sx * lx, hoofH + legR * 0.4, sz * lz,
            legR * 0.72, legR * 0.6, legCol);
          hoof();
        });
      });
    }
  }

  // ---- Barrel body: a horizontal capsule along +z ----
  // Caps inset so the surface still spans ±halfLen (the frontZ/rearZ anchors);
  // `depth` ovalizes the circular section to the authored thickness (a z-axis
  // bone's local z is the world VERTICAL), the bulge is a soft belly.
  const rRump = wid * 0.47;
  const rChest = wid * 0.5;
  bone(0, bodyY, -halfLen + rRump, 0, bodyY, halfLen - rChest,
    rRump, rChest, o.bodyColor, thick / wid, { amount: 0.08, at: 0.45 });

  // ---- Neck + head jutting forward (+z) ----
  const headY = bodyY + thick * 0.28 + (o.neckRaise ?? 0);
  const headZ = halfLen + headSz * (o.neck ?? 0.55);
  const neckAndHead = () => {
    bone(0, bodyY + thick * 0.18, halfLen - headSz * 0.15, 0, headY - headSz * 0.08, headZ - headSz * 0.35,
      thick * 0.28, headSz * 0.4, o.bodyColor); // neck: chest → skull
    // Head ball slightly larger than the old cube's inscribed sphere (×0.58) so
    // doc-layered face parts (eyes/snouts authored against the cube faces) still
    // break the surface.
    ball(0, headY, headZ, headSz * 0.58, headCol); // head
  };
  if (o.neckJoint) limb("neck", [0, bodyY + thick * 0.18, halfLen - headSz * 0.15], neckAndHead);
  else neckAndHead();

  // Empty tail pivot for doc-authored tail geometry (parts with limb: "tail").
  if (o.tailJoint) limb("tail", [0, bodyY + thick * 0.35, -halfLen + 0.05], () => {});

  return {
    headX: 0, headY, headZ,
    headHalf: headSz / 2,
    backY: bodyY + thick / 2,
    frontZ: halfLen,
    rearZ: -halfLen,
    bodyW: wid,
  };
}

/** The archetype's published NAMED anchor points (see ARCHETYPE_ANCHORS in
 *  parts.ts) derived from the geometric QuadAnchors. */
export function quadAnchorPoints(q: QuadAnchors): Record<string, Vec3> {
  const points: Record<string, Vec3> = {
    root: [0, 0, 0],
    head: [q.headX, q.headY, q.headZ],
    muzzle: [q.headX, q.headY, q.headZ + q.headHalf],
    crown: [q.headX, q.headY + q.headHalf, q.headZ],
    back: [0, q.backY, 0],
    front: [0, 0, q.frontZ],
    rear: [0, 0, q.rearZ],
    rump: [0, q.backY, q.rearZ],
    flank: [q.bodyW / 2, 0, 0],
  };
  // Keep the published set in lockstep with the validation list.
  for (const name of ARCHETYPE_ANCHORS.quadruped) {
    if (!(name in points)) throw new Error(`quadruped anchor "${name}" missing`);
  }
  return points;
}
